import { Box, Text, Heading } from '@primer/react';
import { ArrowRightIcon, CheckCircleIcon } from '@primer/octicons-react';
import Reveal from '../components/Reveal';
import ProcessSteps from '../components/ProcessSteps';
import FAQ from '../components/FAQ';
import ServiceCTA from '../components/ServiceCTA';
import SEO from '../components/SEO';
import PageHero from '../components/PageHero';
import { CALENDLY_URL, trackContactClick } from '../utils/links';
import { breadcrumbSchema, faqSchema, SITE_URL } from '../utils/seo';

const models = [
  {
    name: 'Project Engagements',
    badge: 'Fixed scope',
    bestFor: 'A specific problem with a clear start and end. A UX audit, a research sprint, or a focused redesign.',
    timeline: '1 to 6 weeks, depending on scope',
    capacity: 'Scoped and quoted before kickoff, not billed hourly',
    investment: 'Starting at $4,000',
    included: [
      'Fixed scope agreed before kickoff, so you know the total cost upfront',
      'A single senior designer as your point of contact throughout',
      'A final deliverable you own outright, ready to hand to your team',
      'One round of revisions included',
    ],
  },
  {
    name: 'Monthly Retainer',
    badge: 'Starting at $12,000/month',
    highlight: true,
    pill: '🔥 Most Popular',
    bestFor: 'Teams that already have designers but are capacity constrained, under a hiring freeze, or facing a deadline recruiting cannot meet.',
    timeline: 'Ongoing, month to month',
    capacity: 'Up to 40 hours a week of dedicated senior design time',
    investment: 'Starting at $12,000/month, scales with scope',
    included: [
      'Up to 40 hours a week of dedicated senior design time, priced to the scope your roadmap actually needs',
      'Product design, UI, and design systems work, plus strategy and research folded in when a decision needs evidence, not just an opinion',
      'One senior designer as your single point of contact, not a rotating account team',
      'Direct Slack access and a weekly working session, not a monthly status update',
      'Cancel anytime with 30 days notice, no annual contract',
    ],
  },
];

const steps = [
  {
    icon: 'CommentDiscussionIcon',
    title: 'Discovery Call',
    description: 'A 30 minute conversation about your product, your team, and what problem you actually need solved.',
  },
  {
    icon: 'NoteIcon',
    title: 'Proposal & Scope',
    description: 'A written proposal with a fixed scope, timeline, and price. No surprises once work begins.',
  },
  {
    icon: 'RocketIcon',
    title: 'Kickoff',
    description: 'We align on flows, access, and success metrics before any design work starts.',
  },
  {
    icon: 'GraphIcon',
    title: 'Ongoing Delivery',
    description: 'Regular check ins and a steady cadence of shipped work, not a single deliverable at the very end.',
  },
];

const faqs = [
  {
    question: 'How do we decide between a project and a retainer?',
    answer:
      'If you have one specific problem to solve, like an audit or a research sprint, a project engagement is the right fit. If your team ships continuously and needs an embedded design partner month over month, a retainer makes more sense. We will help you figure out which one fits on the discovery call.',
  },
  {
    question: 'Is there a minimum commitment for retainers?',
    answer:
      'Retainers run month to month with 30 days notice to cancel. There is no long term contract required, though most partnerships that work well continue for six months or more.',
  },
  {
    question: 'What if we are not sure yet?',
    answer:
      'Start with a project engagement, like a UX audit. It is the lowest risk way to see how we work before committing to an ongoing retainer.',
  },
  {
    question: 'Do you work with early stage startups, or only enterprise teams?',
    answer:
      'We specialize in enterprise, fintech, AI, logistics, and SaaS products with real complexity, usually teams past the earliest startup stage. If that is not you yet, we are happy to say so on the discovery call.',
  },
  {
    question: 'Why retain an agency instead of just hiring a designer?',
    answer:
      'A $10,000 a month hire is not $10,000 a month once you add payroll tax, benefits, a recruiter fee, and the months of open req and ramp time before they ship anything. A retainer skips all of that, and 30 days notice to cancel beats a severance conversation. It also usually comes out of a different budget line than headcount, so it does not need the same internal approval. If you are deciding between us and your first design hire, hire. We are built for teams that already have designers and need more capacity, not a replacement for one.',
  },
  {
    question: 'What happens after we book a call?',
    answer:
      'We will ask about your product, your team, and your goals, then follow up with a written proposal within a few business days. Nothing is billed until you approve the scope and price.',
  },
];

const schema = [
  breadcrumbSchema([
    { name: 'Home', path: '/' },
    { name: 'How We Work', path: '/how-we-work' },
  ]),
  faqSchema(faqs),
];

function HowWeWork() {
  return (
    <Box sx={{ backgroundColor: 'canvas.default' }}>
      <SEO
        title="How We Work | UX Agency Pricing & Engagement Models"
        description="How Pegasus UX engagements work, from fixed scope projects starting at $4,000 to monthly retainers starting at $12,000 and scaling with scope, for enterprise, fintech, AI, and SaaS teams."
        keywords="ux agency pricing, ux design retainer, hire ux design agency cost, ux consulting pricing, ux agency engagement model"
        path="/how-we-work"
        schema={schema}
        image={`${SITE_URL}/og/how-we-work.jpg`}
      />

      <PageHero
        title="How We Work"
        subtitle="Two ways to work with Pegasus UX, a fixed scope project or an ongoing monthly retainer. Here is exactly what each one includes, so you know what to expect before you ever book a call."
      />

      <Box sx={{ paddingX: ['24px', '48px', '100px'], paddingTop: [6, 7, 8], paddingBottom: [7, 8, 9] }}>
        <Box sx={{ maxWidth: '1400px', margin: '0 auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: ['1fr', '1fr', 'repeat(2, 1fr)'],
            gap: [5, 5, 6],
          }}
        >
          {models.map((model, index) => (
            <Reveal key={model.name} delay={index * 0.1}>
              <Box
                sx={{
                  position: 'relative',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: '20px',
                  border: '1px solid',
                  borderColor: model.highlight ? 'rgba(96, 165, 250, 0.5)' : 'border.default',
                  boxShadow: model.highlight ? '0 24px 60px -32px rgba(96, 165, 250, 0.55)' : 'none',
                  padding: [5, 6, 6],
                }}
              >
                {model.pill && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '-16px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      zIndex: 2,
                      display: 'inline-flex',
                      alignItems: 'center',
                      paddingX: 3,
                      paddingY: '6px',
                      borderRadius: '999px',
                      background: 'linear-gradient(90deg, #60a5fa, #a78bfa)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Text sx={{ fontSize: 0, fontWeight: 700, letterSpacing: '0.04em', color: '#05070a' }}>
                      {model.pill}
                    </Text>
                  </Box>
                )}

                <Box
                  sx={{
                    display: 'inline-flex',
                    alignSelf: 'flex-start',
                    marginBottom: 4,
                    paddingX: 3,
                    paddingY: 1,
                    borderRadius: '999px',
                    border: '1px solid',
                    borderColor: 'border.default',
                  }}
                >
                  <Text sx={{ fontSize: 0, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'fg.muted' }}>
                    {model.badge}
                  </Text>
                </Box>

                <Heading
                  as="h2"
                  sx={{
                    margin: 0,
                    marginBottom: 3,
                    fontFamily: "'Fraunces', serif",
                    fontWeight: 400,
                    fontSize: [3, 4],
                    color: 'fg.default',
                  }}
                >
                  {model.name}
                </Heading>

                <Text as="p" sx={{ margin: 0, marginBottom: 5, fontSize: 1, lineHeight: 1.7, color: 'fg.muted' }}>
                  {model.bestFor}
                </Text>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text sx={{ fontSize: 0, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'fg.muted' }}>
                      Timeline
                    </Text>
                    <Text sx={{ fontSize: 1, color: 'fg.default', textAlign: 'right' }}>{model.timeline}</Text>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text sx={{ fontSize: 0, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'fg.muted' }}>
                      Capacity
                    </Text>
                    <Text sx={{ fontSize: 1, color: 'fg.default', textAlign: 'right', maxWidth: '65%' }}>{model.capacity}</Text>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text sx={{ fontSize: 0, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'fg.muted' }}>
                      Investment
                    </Text>
                    <Text sx={{ fontSize: 1, color: 'fg.default', textAlign: 'right' }}>{model.investment}</Text>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 5 }}>
                  {model.included.map((item) => (
                    <Box key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Box sx={{ marginTop: '2px', flexShrink: 0 }}>
                        <CheckCircleIcon size={16} fill="#60a5fa" />
                      </Box>
                      <Text sx={{ fontSize: 1, color: 'fg.default' }}>{item}</Text>
                    </Box>
                  ))}
                </Box>

                <Box
                  as="a"
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={trackContactClick}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    marginTop: 'auto',
                    paddingX: 4,
                    paddingY: 2,
                    borderRadius: '6px',
                    border: '1px solid rgba(27, 31, 36, 0.15)',
                    backgroundColor: '#1f883d',
                    color: '#ffffff',
                    fontSize: 1,
                    fontWeight: 600,
                    textDecoration: 'none',
                    transition: 'background-color 0.15s ease',
                    ':hover': { backgroundColor: '#1c8139' },
                    ':active': { backgroundColor: '#197935' },
                  }}
                >
                  Schedule a Consultation
                  <ArrowRightIcon size={16} />
                </Box>
              </Box>
            </Reveal>
          ))}
        </Box>

        <Text as="p" sx={{ margin: 0, marginTop: 5, fontSize: 0, color: 'fg.muted', textAlign: 'center' }}>
          Final scope and pricing depend on your product's complexity. The figures above reflect
          typical starting points, not a fixed rate card.
        </Text>
        </Box>
      </Box>

      <ProcessSteps
        steps={steps}
        eyebrow="Getting Started"
        title="How an engagement actually starts."
      />

      <FAQ eyebrow="FAQ" title="Common questions about working with us." items={faqs} />

      <ServiceCTA
        eyebrow="Ready when you are."
        headline={[
          { text: 'Tell us about your', tone: 'plain' },
          { text: 'product, and your goals.', tone: 'gradient' },
        ]}
        subtitle="Book a discovery call and we will help you figure out which engagement model fits."
      />
    </Box>
  );
}

export default HowWeWork;
